// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2026 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import useAppDispatch from "@insite/client-framework/Common/Hooks/useAppDispatch";
import useAppSelector from "@insite/client-framework/Common/Hooks/useAppSelector";
import mergeToNew from "@insite/client-framework/Common/mergeToNew";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { HasCartLineContext, withCartLine } from "@insite/client-framework/Components/CartLineContext";
import { Cart } from "@insite/client-framework/Services/CartService";
import translate from "@insite/client-framework/Translate";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import TextArea, { TextAreaProps } from "@insite/mobius/TextArea";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import { xCommonUpdate } from "@vanmeter/App/Handlers/XNG/Common";
import { CustomAppState } from "@vanmeter/App/Store/Reducers";
import Check from "@vanmeter/XDL/Icons/IconCheck";
import Edit from "@vanmeter/XDL/Icons/IconEdit";
import Plus from "@vanmeter/XDL/Icons/IconPlus";
import { createLinkIconStyles } from "@vanmeter/XDL/Utilities";
import React, { FC, useEffect, useState } from "react";
import { css } from "styled-components";

interface OwnProps {
    cart: Cart;
    /**
     * If true, the notes will display in a text field for editing.
     * If false, the notes will display as read-only text.
     * Default value: false
     */
    editable?: boolean;
    onNotesChange?: (updatedNotes: string) => void;
    label?: string;
    extendedStyles?: CartLineNotesStyles;
    // XNG-Changes
    hideLink?: boolean;
    hideField?: boolean;
    onToggleNotesField?: (visible: boolean) => void;
    cartNotes?: string;
}

type Props = OwnProps & HasCartLineContext;

export interface CartLineNotesStyles {
    wrapper?: InjectableCss;
    /** @deprecated */
    toggleLink?: LinkPresentationProps;
    label?: TypographyPresentationProps;
    editableNotesTextArea?: TextAreaProps;
    readOnlyNotesText?: TypographyPresentationProps;
}

export const cartLineNotesStyles: CartLineNotesStyles = {
    wrapper: {
        css: css`
            width: 100%;
        `,
    },
    editableNotesTextArea: {
        css: css`
            min-width: 100%;
            max-width: 100%;
            min-height: 50px;
        `,
    },
    label: {
        css: css`
            button {
                color: #2e64b0;
                background: none;
                border: none;
                padding: 0;
                font-size: 15px;
                cursor: pointer;
            }
        `,
    },
};

const CartLineNotes: FC<Props> = ({
    cart,
    cartLine,
    editable = false,
    onNotesChange = () => {},
    label,
    extendedStyles,
    // XNG-Change
    hideLink = false,
    hideField = false,
    onToggleNotesField,
    cartNotes,
}) => {
    const [showLineNotes, setShowLineNotes] = useState(false);
    // <XNG-changes>
    const cartLineNotes = useAppSelector(state => (state as CustomAppState).xCommon[`cartLineNotes${cartLine?.id}`]);
    const dispatch = useAppDispatch();

    useEffect(() => {
        dispatch(xCommonUpdate({ key: `cartLineNotes${cartLine?.id}`, value: cartLine?.notes }));
    }, []);
    // </XNG-changes>

    const lineNotesClickHandler = () => {
        setShowLineNotes(!showLineNotes);
        // XNG-Changes
        onToggleNotesField?.(!showLineNotes);
        if (showLineNotes) {
            onNotesChange(cartLineNotes);
        }
    };

    // const [notes, setNotes] = useState(cartLine.notes);

    const changeHandler = (event: React.ChangeEvent<HTMLTextAreaElement>) => {
        // setNotes(event.currentTarget.value);
        dispatch(xCommonUpdate({ key: `cartLineNotes${cartLine.id}`, value: event.currentTarget.value }));
    };

    const blurHandler = () => {
        onNotesChange(cartLineNotes);
    };

    // XNG-changes: update label
    const heading = showLineNotes
        ? "Save Item Comment"
        : cartLine?.notes?.length === 0
          ? "Add Item Comment"
          : "Edit Item Comment";

    const [styles] = React.useState(() => mergeToNew(cartLineNotesStyles, extendedStyles));

    // XNG-Changes
    const hasNotes = cartLine?.notes?.length > 0;

    // XNG-Changes: add link
    if (editable) {
        return (
            <StyledWrapper {...styles.wrapper}>
                {!hideLink && (
                    <Link
                        {...createLinkIconStyles({
                            icon: showLineNotes ? Check : hasNotes ? Edit : Plus,
                            iconSize: { width: 11 },
                            sizeVariant: "xsmall",
                        })}
                        onClick={lineNotesClickHandler}
                        // XNG-Changes
                        aria-label={translate(heading)}
                    >
                        {translate(heading)}
                    </Link>
                )}
                {/* <Typography
                    {...styles.label}
                    htmlFor={`notes-${cartLine.id}`}
                    forwardAs="label"
                    onClick={lineNotesClickHandler}
                    data-test-selector="cartline_notesShowHideButton"
                >
                    <button>{translate(heading)}</button>
                </Typography> */}
                {!hideField && (
                    <TextArea
                        {...styles.editableNotesTextArea}
                        value={cartLineNotes}
                        id={`notes-${cartLine.id}`}
                        disabled={!cart.canModifyOrder || !editable}
                        onChange={changeHandler}
                        // onBlur={blurHandler}
                        data-test-selector="cartline_notes"
                        // XNG-Changes
                        placeholder={translate("Add any item comments here...")}
                        label={translate("Item Comment")}
                    />
                )}
            </StyledWrapper>
        );
    }

    // XNG-Change
    if (!cartLine?.notes && !cartNotes) {
        return null;
    }

    // XNG-Changes: use Typography
    return <Typography {...styles.readOnlyNotesText}>{cartNotes || cartLine?.notes}</Typography>;
};

export default withCartLine(CartLineNotes);
